/**
 * Inventory-aware share messages ("Market This Menu", folded into the one Share window).
 * Pure: reads only the menu's own listing fields (strain, form, quantity, price, added date).
 * Never reads or prints supplier/grower identity — the input type has no field for it.
 * Fewer than 2 usable listings → the static templates in menuShareReady.ts.
 */
import { shareTemplates } from '@/lib/menuShareReady';

export interface PitchListing {
  strain_name: string | null;
  product_form: string | null;
  quantity_grams: number | null;
  price_per_gram: number | null;
  created_at?: string | null;
}

export interface ShareTemplate { id: string; label: string; text: string }

const G_PER_LB = 453.592;
const isFlower = (form: string | null) => !!form && /flower|smalls|bud|trim|shake/i.test(form);
const clean = (s: string | null) => (s ?? '').trim();

function priceText(l: PitchListing): string | null {
  if (!l.price_per_gram || l.price_per_gram <= 0) return null;
  return isFlower(l.product_form)
    ? `$${Math.round(l.price_per_gram * G_PER_LB).toLocaleString('en-US')}/lb`
    : `$${(Math.round(l.price_per_gram * 100) / 100).toLocaleString('en-US')}/g`;
}

const joinNames = (names: string[]) =>
  names.length <= 1 ? names.join('') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;

export function menuSharePitch(menuName: string, listings: PitchListing[]): ShareTemplate[] {
  const usable = listings.filter(l => clean(l.strain_name));
  if (usable.length < 2) return shareTemplates(menuName, listings.length);

  // Headline strains: freshest first, then biggest quantity; unique names.
  const ranked = [...usable].sort((a, b) =>
    (Date.parse(b.created_at ?? '') || 0) - (Date.parse(a.created_at ?? '') || 0)
    || (b.quantity_grams ?? 0) - (a.quantity_grams ?? 0));
  const names: string[] = [];
  for (const l of ranked) {
    const n = clean(l.strain_name);
    if (!names.some(x => x.toLowerCase() === n.toLowerCase())) names.push(n);
    if (names.length === 2) break;
  }
  const lead = ranked.find(l => clean(l.strain_name) === names[0])!;
  const leadPrice = priceText(lead);

  // Starting price among flower listings priced per lb (comparable units only).
  const flowerPrices = usable.filter(l => isFlower(l.product_form) && (l.price_per_gram ?? 0) > 0).map(l => l.price_per_gram!);
  const startLb = flowerPrices.length ? `$${Math.round(Math.min(...flowerPrices) * G_PER_LB).toLocaleString('en-US')}/lb` : null;
  const flowerLbs = Math.floor(usable.filter(l => isFlower(l.product_form)).reduce((t, l) => t + (l.quantity_grams ?? 0), 0) / G_PER_LB);

  const strains = joinNames(names);
  const priceBit = leadPrice ? ` (${leadPrice})` : '';
  const count = usable.length;

  return [
    { id: 'fresh', label: 'VIP Drop', text: `Hey, putting this in front of you first before it moves: fresh ${strains}${priceBit} just landed. Here’s our live menu with photos and wholesale pricing: [link]. Hit me back if you want to lock anything in or grab samples.` },
    { id: 'bulk', label: 'Bulk & Volume', text: flowerLbs >= 1
      ? `We’ve got ${flowerLbs} lbs moving this week across ${count} listings${startLb ? `, starting at ${startLb}` : ''}. Full availability and test results here: [link]. Tell me what volume you need.`
      : `We’ve got ${count} listings live right now, including ${strains}. Full availability and test results here: [link]. Tell me what volume you need.` },
    { id: 'short', label: 'Quick Text', text: `${names[0]}${leadPrice ? ` at ${leadPrice}` : ''} just hit our menu: [link]. Grab samples before it moves.` },
  ];
}
